/* eslint-env browser, jquery */
import debugDep from 'debug';
import page from 'page';
import _ from 'lodash';
import Promise from 'bluebird';
import metrics from 'modules/metrics';
import bus from 'modules/npm-component/bus';
import CookieMetrics from 'modules/ab-express/cookie-metrics';

const debug = debugDep('auth0:ab');
/**
 * AbTestingWrapper constructor
 */

function AbTestingWrapper() {
  if (!(this instanceof AbTestingWrapper)) {
    const wrapper = new AbTestingWrapper();
    wrapper.initialize = Promise.resolve();

    return wrapper;
  }
}

/**
 * Retrieve `window.abTestingLib`
 */

AbTestingWrapper.prototype.lib = function() {
  return window.abTestingLib;
};

AbTestingWrapper.prototype.computeExperiments = function computeExperiments() {
  const _this = this;
  const ab = _this.lib();
  const metricsLib = metrics.lib();

  // If metrics or ab fails, do nothing
  if (!metricsLib || !ab || !ab.integration) return (ctx, next) => next();

  ab.integration('auth0metrics').start({ metrics: metricsLib });
  _this.initialize = ab.start().then(() => {
    enhanceAnalytics(ab.analytics);
  });

  return function(ctx, next) {
    ctx.winningFullPageVariantName = 'original';

    _this.initialize.then(() => {
      try {
        const pathname = document.location.pathname;
        const experiments = ab.getExperiments().runAllByPath(pathname).experiments || [];
        ctx.winningVariants = experiments.map(e =>
          _.assign(e.getCurrentVariant(), {
            pathname,
            experiment: e.name,
            fullPage: e.name.indexOf('full-page-ab') === 0
          })
        );
        ctx.winningFullPageVariantName = getWinningFullPageVariantName(ctx.winningVariants);
      } catch (e) {
        debug('Error running experiments for %s', ctx.path);
      }

      next();
    });
  };
};

/**
 * Execute javascript code for each winning variant
 */

AbTestingWrapper.prototype.executeExperiments = function executeExperiments() {
  const _this = this;

  return function(ctx, next) {
    _this.initialize.then(() => {
      if (ctx.notFound) {
        return next();
      }

      debug('Computing experiments for %s', ctx.pathname);

      if (!ctx.winningVariants) {
        return;
      }

      try {
        const pathname = document.location.pathname;
        const variantContext = { $, bus, page };

        ctx.winningVariants.forEach(variant => {
          const properties = variant.getPropertiesByType('js');
          if (properties && properties[0] && variant.pathname === pathname) {
            properties[0].runInContext(variantContext);
          }
        });
      } catch (e) {
        debug('Error computing experiments for %s', ctx.path);
      }
    });
  };
};

AbTestingWrapper.prototype.executeExperiment = function executeExperiment(
  experimentName,
  callback
) {
  const _this = this;
  const ab = _this.lib();

  _this.initialize.then(() => {
    debug('Running single experiment %s', experimentName);

    if (!experimentName) {
      return callback && callback(null);
    }

    const experiment = ab.experiments.findByName(experimentName);
    if (!experiment) {
      return callback && callback(null);
    }

    const variant = experiment.run();
    const properties = variant.getPropertiesByType('js');

    try {
      if (properties && properties[0]) {
        const variantContext = { $, bus, page };
        properties[0].runInContext(variantContext);
      }
    } catch (e) {
      debug('Error computing experiment %s', experimentName);
    }

    return callback && callback(variant);
  });
};

function enhanceAnalytics(analytics) {
  const cookieAnalytics = new CookieMetrics();

  const originalSetCurrentVariantName = analytics.setCurrentVariantName.bind(analytics);
  const originalGetCurrentVariantName = analytics.getCurrentVariantName.bind(analytics);

  const enhancedSetCurrentVariantName = function(variantName, value) {
    cookieAnalytics.setCurrentVariantName(variantName, value);
    originalSetCurrentVariantName(variantName, value);
  };

  const enhancedGetCurrentVariantName = function(variantName) {
    const originalValue = originalGetCurrentVariantName(variantName);
    const cookieValue = cookieAnalytics.getCurrentVariantName(variantName);

    if (originalValue !== cookieValue) {
      originalSetCurrentVariantName(variantName, cookieValue);
    }

    return cookieValue;
  };

  analytics.setCurrentVariantName = enhancedSetCurrentVariantName;
  analytics.getCurrentVariantName = enhancedGetCurrentVariantName;
  window.setVariant = enhancedSetCurrentVariantName;
  window.getVariant = enhancedGetCurrentVariantName;
}

function getWinningFullPageVariantName(variants) {
  const winningFullPageVariant = variants.filter(v => v.fullPage)[0];

  return winningFullPageVariant ? winningFullPageVariant.name : 'original';
}

/**
 * Expose `AbTestingWrapper` constructor
 */

export default AbTestingWrapper();



// WEBPACK FOOTER //
// ./src/modules/ab-testing/ab-testing.js